import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import fr from 'facet-rest';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import MetaTable from 'Components/UI/Meta/MetaTable';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import {
  getManagedAccountAllocationCleanupTableFilters,
} from 'Views/Page/Content/Templates/ManagedAccountAllocationCleanupTemplate/MetaOverrideSet';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import Typography from 'Components/UI/Typography';
import useUserContext from 'Context/UserContext';
import Button from 'Components/UI/Button';

const styles = theme => ({
  root: {},
  danger: {
    backgroundColor: theme.palette.facet.accentRed,
  },
  needsAttention: {
    backgroundColor: theme.palette.facet.accentYellow1,
  },
  needsOpsAttention: {
    backgroundColor: theme.palette.facet.accentBlue15,
  },
  legend: {
    marginTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.facet.mediumGray}`,
  },
  downloadButton: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    marginRight: theme.spacing(1),
    maxWidth: 300,
    alignSelf: 'flex-end',
  },
  line: {
    display: 'flex',
    flexDirection: 'row',
    height: 60,
    alignItems: 'center',
  },
  key: {
    marginRight: theme.spacing(1),
    width: 18,
    height: 18,
    minWidth: 18,
    minHeight: 18,
    border: `2px solid ${theme.palette.facet.primaryGray}`,
    borderRadius: '2px',
  },
});

const getColumns = (isSuperUser, isOps) => {
  const columns = [
    { name: 'householdId' },
    { name: 'leadPlannerId' },
    { name: 'name' },
    { name: 'suggestedModel' },
    { name: 'selectedModel' },
    { name: 'currentModel' },
  ];

  // set conditional columns based on who is logged in
  if (isSuperUser) {
    columns.push({ name: 'modelDaysOutOfSync' });
    columns.push({ name: 'currentModelDaysOutOfSync' });
    columns.push({ name: 'deviationJustification' });
    columns.push({ name: 'deviationJustificationAdditionalDetails' });
  } else if (isOps) {
    columns.push({ name: 'currentModelDaysOutOfSync' });
    columns.push({ name: 'deviationJustification' });
    columns.push({ name: 'inOpsNotes' });
  } else {
    columns.push({ name: 'modelDaysOutOfSync' });
    columns.push({ name: 'deviationJustification' });
    columns.push({ name: 'deviationJustificationAdditionalDetails' });
  }

  columns.push({ name: 'assignedInOpsAgentId' });

  return columns;
};

const ManagedAccountAllocationCleanupTemplate = ({
  className,
  classes,
  collection,
}) => {
  const { householdSearch } = useSimpleHousehold();
  const { loadModalCollectionByLink } = useModalContext();
  const { isInvestmentOps, hasSuperUserRole } = useUserContext();

  let downloadComplianceLink = null;

  if (collection?.resources().length() > 0) {
    downloadComplianceLink = collection.resources().head().relationship(fr.relationships.randomHouseholdsForComplianceExport.name)?.link('related')
      .url();
  }

  function rowClassesResolver(resource) {
    const isSelectedModelOutOfSync = parseInt(resource.attr('modelDaysOutOfSync'), 10) >= 15;
    const isCurrentModelOutOfSync = parseInt(resource.attr('currentModelDaysOutOfSync'), 10) >= 15;

    // color the row based on logged in user
    if (hasSuperUserRole && (isSelectedModelOutOfSync || isCurrentModelOutOfSync)) {
      return classNames(classes.danger);
    } if (isInvestmentOps && isCurrentModelOutOfSync) {
      return classNames(classes.danger);
    } if (!isInvestmentOps && isSelectedModelOutOfSync) {
      return classNames(classes.danger);
    }

    if (resource.attr('needsAttention') === 'true') {
      return classNames(classes.needsAttention);
    }
    if (resource.attr('needsOpsAttention') === 'true') {
      return classNames(classes.needsOpsAttention);
    }
    return null;
  }

  return (
    <>
      { downloadComplianceLink && (
        <div className={classes.downloadButton}>
          <Button onClick={() => downloadComplianceLink && window.open(downloadComplianceLink, '_blank')} variant="contained" color="primary" className={classes.button}>
            {'Download Compliance Report'}
          </Button>
        </div>
      )}
      <MetaTable
        className={classNames(classes.root, className)}
        isPageView
        collection={collection}
        columns={getColumns(hasSuperUserRole, isInvestmentOps)}
        tableMetaOverrideSetName="ManagedAccountAllocationCleanupTableOverride"
        hasMetaQueryForm
        QueryFormProps={{
          getAvailableFilters: coll => getManagedAccountAllocationCleanupTableFilters(coll, householdSearch, collection.meta().field('leadPlannerId').options().list(), collection.meta().field('assignedInOpsAgentId').options().list(), collection.meta().field('currentModel').options().list()),
          AdditionalActions: (
            <Box className={classes.legend}>
              <Box className={classes.line}>
                <div className={classNames(classes.key, classes.danger)} />
                <Typography className={classes.text}>{'Out of sync days >= 15'}</Typography>
              </Box>
              <Box className={classes.line}>
                <div className={classNames(classes.key, classes.needsAttention)} />
                <Typography
                  className={classes.text}
                >{'Needs Planner - Suggested Model and Selected Model don\'t match'}
                </Typography>
              </Box>
              <Box className={classes.line}>
                <div className={classNames(classes.key, classes.needsOpsAttention)} />
                <Typography
                  className={classes.text}
                >{'Needs Investment Ops - Selected Model and Current Model don\'t match'}
                </Typography>
              </Box>
            </Box>
          ),
        }}
        hasSortableColumns
        linkQueryParamName={'pageSelfLink'}
        features={{
          onClick: (resource) => loadModalCollectionByLink(resource.relationship(fr.relationships.managedAccountAllocation.name).link('related').url()),
        }}
        RowProps={{
          classesResolver: rowClassesResolver,
        }}
      />
    </>
  );
};

ManagedAccountAllocationCleanupTemplate.defaultProps = {
  className: '',
};

ManagedAccountAllocationCleanupTemplate.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  collection: PropTypes.instanceOf(fr.Collection).isRequired,
};

export default withStyles(styles)(ManagedAccountAllocationCleanupTemplate);
